import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type'
import { TextListTemplate, TextListTemplateType } from '../textList/textListTemplate'
import { createDriverTraining } from '../../../apollo/mutations/createDriverTraining'
import { RootState } from '../../../store/store'
import { addNotification } from '../../../store/slices/notification/notificationSlice'
import useLanguage from '../../../contexts/language/LanguageContext'
import { getTranslationByPath } from '../../../utils/translations'

export type BozpInfoTrainingTemplateType = TextListTemplateType & {
  transitionUrl: TransitionUrl
  trainingSaveFailedText: TranslationPath
}

type Props = {
  page: Page<BozpInfoTrainingTemplateType>
}

export const BozpInfoTrainingTemplate = (props: Props): React.ReactElement => {
  const [isSaving, setIsSaving] = useState(false)
  const language = useLanguage()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const fetchedTransportation = useSelector((state: RootState) => state.transportation.fetchedTransportation)
  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm)

  const driverName = transportationForm.driverName || fetchedTransportation?.driverName || ''
  const phoneNumber = transportationForm.phoneNumber || fetchedTransportation?.phoneNumber || ''
  const idCardNumber = transportationForm.idCardNumber
    || (fetchedTransportation?.driverIdType === 'idCard' ? fetchedTransportation?.driverIdNo : '')
    || ''
  const passportNumber = transportationForm.passportNumber
    || (fetchedTransportation?.driverIdType === 'passport' ? fetchedTransportation?.driverIdNo : '')
    || ''
  const driverIdNo = idCardNumber || passportNumber
  const driverIdType = idCardNumber
    ? 'idCard'
    : passportNumber
      ? 'passport'
      : fetchedTransportation?.driverIdType || 'idCard'

  const onContinue = async () => {
    if (!driverName || !phoneNumber || !driverIdNo || !driverIdType) {
      dispatch(addNotification({
        type: 'error',
        title: getTranslationByPath(language.translations, props.page.data.trainingSaveFailedText),
      }))
      return
    }

    try {
      setIsSaving(true)
      const result = await createDriverTraining({
        driverName,
        phoneNumber,
        driverIdNo,
        driverIdType,
      })
      if (result.data?.createDriverTraining) {
        navigate(props.page.data.transitionUrl)
        return
      }
    } catch (e) {
      // Ignore and show the same user-facing error below.
    }

    dispatch(addNotification({
      type: 'error',
      title: getTranslationByPath(language.translations, props.page.data.trainingSaveFailedText),
    }))
    setIsSaving(false)
  }

  const pageWithHandler = useMemo(
    () => ({
      ...props.page,
      data: {
        ...props.page.data,
        onContinue,
        disabledContinueBtn: isSaving,
      },
    }),
    [props.page, isSaving],
  )

  return <TextListTemplate page={pageWithHandler} />
}
